import { useEffect, useState } from "react";
import { Phone } from "lucide-react";
import { createElement } from "react";
import { useBackgroundServiceManager, useDockWidgetManager, type DockWidget } from "@slumbr/app-sdk";

import { isWidgetEnabled, useDockStore } from "./dock-store";

/**
 * Bridge legacy background-services that expose `persistentUI` (e.g. the active
 * call) into contextual dock widgets, so they live in the dock without each app
 * needing to be rewritten as a DockWidget. A service only exists while relevant
 * (the call service registers during a call), so these are always contextual.
 */
function bridgedServiceWidgets(
  services: Map<string, { id: string; appId: string; persistentUI?: React.ComponentType }>,
): DockWidget[] {
  const out: DockWidget[] = [];
  for (const s of services.values()) {
    if (!s.persistentUI) continue;
    out.push({
      id: `svc:${s.id}`,
      appId: s.appId,
      title: s.appId === "messenger" ? "Call" : s.appId,
      icon: createElement(Phone, { className: "h-4 w-4" }),
      contextual: true,
      PanelUI: s.persistentUI,
      CompactUI: s.persistentUI,
    });
  }
  return out;
}

/** Re-render every `ms` so badge()/isAvailable() (which read external stores) stay fresh. */
export function useDockTick(ms = 1500): void {
  const [, force] = useState(0);
  useEffect(() => {
    const t = setInterval(() => force((n) => n + 1), ms);
    return () => clearInterval(t);
  }, [ms]);
}

/**
 * Resolve the widgets that should currently show, in display order:
 * contextual widgets (auto, gated by isAvailable) followed by enabled
 * non-contextual widgets in the user's order.
 */
export function resolveVisibleWidgets(
  all: DockWidget[],
  enabled: Record<string, boolean>,
  order: string[],
): DockWidget[] {
  const contextual = all.filter((w) => w.contextual && (w.isAvailable?.() ?? true));
  const always = all.filter((w) => !w.contextual && isWidgetEnabled(enabled, w.id));
  always.sort((a, b) => {
    const ia = order.indexOf(a.id);
    const ib = order.indexOf(b.id);
    return (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib);
  });
  return [...always, ...contextual];
}

export function useVisibleWidgets(): DockWidget[] {
  useDockTick();
  const widgets = useDockWidgetManager((s) => s.widgets);
  const services = useBackgroundServiceManager((s) => s.services);
  const enabled = useDockStore((s) => s.enabled);
  const order = useDockStore((s) => s.order);
  const all = [...Array.from(widgets.values()), ...bridgedServiceWidgets(services)];
  return resolveVisibleWidgets(all, enabled, order);
}

/** Always-available (non-contextual) widgets, for the settings manage page. */
export function useManageableWidgets(): DockWidget[] {
  const widgets = useDockWidgetManager((s) => s.widgets);
  const order = useDockStore((s) => s.order);
  const list = Array.from(widgets.values()).filter((w) => !w.contextual);
  list.sort((a, b) => {
    const ia = order.indexOf(a.id);
    const ib = order.indexOf(b.id);
    return (ia === -1 ? Number.MAX_SAFE_INTEGER : ia) - (ib === -1 ? Number.MAX_SAFE_INTEGER : ib);
  });
  return list;
}
